import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Briefcase, Eye, LineChart, Mail, RefreshCw, X } from 'lucide-react';
import type { VirtualAccountSummary } from '@pnl/shared';
import { api } from '../../lib/api';
import { resultBadge } from '../../lib/accountStatus';
import { StatusBadge } from './StatusBadge';

interface Props {
  account: VirtualAccountSummary;
}

export function AccountHeader({ account }: Props) {
  const navigate = useNavigate();
  const [credOpen, setCredOpen] = useState(false);
  const badge = resultBadge(account.result, account.phase);

  const actions = [
    { label: 'Credenciais', icon: Eye, onClick: () => setCredOpen(true) },
    { label: 'Contacte-nos', icon: Mail, onClick: () => navigate('/support') },
    { label: 'Atualizar', icon: RefreshCw, onClick: () => window.location.reload() },
    { label: 'Minhas Contas', icon: Briefcase, onClick: () => navigate('/accounts') },
  ];

  return (
    <div className="border-ink-800 bg-ink-900 rounded-xl border p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="bg-brand-500/15 text-brand-400 grid h-9 w-9 place-items-center rounded-lg">
            <LineChart size={16} />
          </div>
          <div>
            <p className="text-2xs text-ink-400 font-semibold uppercase tracking-widest">
              Conta PnL
            </p>
            <h2 className="text-ink-100 text-base font-semibold">
              {account.label} · #{account.subAccountNelogicaId}
            </h2>
          </div>
        </div>
        <StatusBadge label={badge.label} tone={badge.tone} />
      </div>

      <div className="border-ink-800 mt-4 flex flex-wrap gap-2 border-t pt-4">
        {actions.map((a) => (
          <button
            key={a.label}
            type="button"
            className="border-ink-700 bg-ink-800 hover:bg-ink-700 text-ink-200 inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors"
            onClick={a.onClick}
          >
            <a.icon size={13} />
            {a.label}
          </button>
        ))}
      </div>

      {credOpen && <CredenciaisModal onClose={() => setCredOpen(false)} />}
    </div>
  );
}

function splitName(full: string | null): { first: string; last: string } {
  if (!full || !full.trim()) return { first: '—', last: '—' };
  const parts = full.trim().split(/\s+/);
  if (parts.length === 1) return { first: parts[0]!, last: '—' };
  return { first: parts[0]!, last: parts.slice(1).join(' ') };
}

function CredenciaisModal({ onClose }: { onClose: () => void }) {
  const { data } = useQuery({ queryKey: ['trader-me'], queryFn: () => api.getTraderMe() });
  const name = splitName(data?.user.fullName ?? null);
  const profile = data?.profile ?? null;

  const rows: { label: string; value: string; locked?: boolean }[] = [
    { label: 'Nome', value: name.first },
    { label: 'Sobrenome', value: name.last },
    { label: 'Email', value: data?.user.email ?? '—', locked: true },
    { label: 'Telefone', value: profile?.phone ?? '—' },
    { label: 'País', value: profile?.country ?? '—' },
    { label: 'Cidade', value: profile?.city ?? '—' },
    { label: 'Endereço', value: profile?.address ?? '—' },
  ];

  // Portal pro body: garante que o `fixed inset-0` seja relativo à viewport mesmo
  // se algum ancestral tiver transform/filter (senão o modal "cai" pro fim da página).
  return createPortal(
    <div
      className="bg-ink-950/70 fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="border-ink-700 bg-ink-900 w-full max-w-md rounded-xl border shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-ink-800 flex items-center justify-between border-b px-5 py-4">
          <div>
            <p className="text-2xs text-ink-400 font-semibold uppercase tracking-widest">Perfil</p>
            <h3 className="text-ink-100 text-base font-semibold">Credenciais</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-ink-400 hover:text-ink-100 transition-colors"
            aria-label="Fechar"
          >
            <X size={18} />
          </button>
        </div>
        <div className="space-y-3 px-5 py-4">
          {rows.map((r) => (
            <div key={r.label} className="flex items-start justify-between gap-4">
              <span className="text-2xs text-ink-400 font-semibold uppercase tracking-wide">
                {r.label}
              </span>
              <span className="text-ink-100 max-w-[62%] text-right text-sm">
                {r.value}
                {r.locked && <span className="text-ink-500 text-2xs ml-1">(não editável)</span>}
              </span>
            </div>
          ))}
          <p className="text-2xs text-ink-500 border-ink-800 mt-2 border-t pt-3 leading-relaxed">
            Email e senha de acesso não podem ser alterados por aqui. Fale com o suporte se precisar
            atualizar seus dados.
          </p>
        </div>
      </div>
    </div>,
    document.body,
  );
}
